import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, ExternalLink, Linkedin } from "lucide-react";

import logo from "@/assets/cellza-logo.webp";
import { mockContent } from "@/mock";

import { Button } from "@/components/ui/button";
/* Avatar import removed (unused) */

/* useTransparentLogo removed (logo is already transparent) */
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
/* Dialog import removed (unused) */


const MolecularSigil = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 1200 800"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* symmetric, abstract molecular outlines - ENHANCED for more visibility */}
    <g opacity="0.95">
      <circle cx="600" cy="360" r="170" stroke="rgba(255,255,255,0.14)" strokeWidth="1.5" />
      <circle cx="600" cy="360" r="240" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
      <circle cx="600" cy="360" r="310" stroke="rgba(198,98,191,0.12)" strokeWidth="1" />

      {/* Nodes - ENHANCED colors */}
      {[
        [600, 130, "rgba(198,98,191,0.42)"],
        [600, 590, "rgba(97,221,152,0.38)"],
        [420, 300, "rgba(255,255,255,0.16)"],
        [780, 300, "rgba(255,255,255,0.16)"],
        [450, 470, "rgba(97,221,152,0.12)"],
        [750, 470, "rgba(198,98,191,0.12)"],
      ].map(([x, y, c], idx) => (
        <circle key={idx} cx={x} cy={y} r="9" fill={c} />
      ))}

      {/* Links - ENHANCED */}
      <path d="M600 130 L420 300 L450 470 L600 590 L750 470 L780 300 Z" stroke="rgba(255,255,255,0.14)" strokeWidth="1.5" />
      <path d="M420 300 L780 300" stroke="rgba(198,98,191,0.24)" strokeWidth="1.5" />
      <path d="M450 470 L750 470" stroke="rgba(97,221,152,0.22)" strokeWidth="1.5" />

      {/* Faint beams - ENHANCED */}
      <path d="M190 240 C360 160, 500 150, 620 190" stroke="rgba(198,98,191,0.14)" strokeWidth="2" />
      <path d="M1020 520 C850 610, 720 620, 580 585" stroke="rgba(97,221,152,0.12)" strokeWidth="2" />
    </g>
  </svg>
);

const HeroMoleculeHalo = ({ className }) => (
  <svg className={className} viewBox="0 0 1200 800" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Symmetrical assembly ring + sparse nodes/links (hybrid with cells) */}
    <g opacity="1">
      <circle cx="600" cy="360" r="210" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
      <circle cx="600" cy="360" r="260" stroke="rgba(255,255,255,0.045)" strokeWidth="1" />
      <circle cx="600" cy="360" r="310" stroke="rgba(198,98,191,0.035)" strokeWidth="1" />

      {/* crosshair / construction ticks */}
      <path d="M600 35 V120" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
      <path d="M600 600 V685" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
      <path d="M275 360 H360" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
      <path d="M840 360 H925" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />

      {/* nodes */}
      {[
        [600, 150, "rgba(198,98,191,0.22)", 7],
        [600, 570, "rgba(97,221,152,0.18)", 7],
        [445, 265, "rgba(255,255,255,0.10)", 5],
        [755, 265, "rgba(255,255,255,0.10)", 5],
        [485, 470, "rgba(255,255,255,0.07)", 4],
        [715, 470, "rgba(255,255,255,0.07)", 4],
      ].map(([x, y, c, r], idx) => (
        <circle key={idx} cx={x} cy={y} r={r} fill={c} />
      ))}

      {/* links */}
      <path
        d="M600 150 L445 265 L485 470 L600 570 L715 470 L755 265 Z"
        stroke="rgba(255,255,255,0.075)"
        strokeWidth="1"
      />
      <path d="M445 265 L755 265" stroke="rgba(198,98,191,0.10)" strokeWidth="1" />
      <path d="M485 470 L715 470" stroke="rgba(97,221,152,0.09)" strokeWidth="1" />
    </g>
  </svg>
);


const RouteBackground = () => {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const isFocusAreas = pathname === "/focus-areas";
  const isCollab = pathname === "/collaborate";

  // common base - ENHANCED for more vibrant colors
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10">
      {/* matte base with deeper color */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(8,14,28,1)_0%,rgba(0,0,0,1)_65%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(1400px_900px_at_50%_15%,rgba(18,30,52,0.75),transparent_58%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(1100px_800px_at_12%_62%,rgba(97,221,152,0.09),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(1200px_850px_at_88%_58%,rgba(198,98,191,0.10),transparent_58%)]" />
      {/* Bioluminescent cells (A) + subtle metallic finish */}
      <div className="absolute inset-0 cells-layer" />
      <div className="absolute inset-0 bokeh-layer" />
      <div className="absolute inset-0 metal-sheen" />
      <div className="absolute inset-0 metal-bloom" />
      <div className="absolute inset-0 metal-specular" />
      <div className="absolute inset-0 metal-ripple" />
      <div className="absolute inset-0 metal-brush" />

      {/* page-specific tonal gradient - ENHANCED */}
      {isHome && (
        <>
          {/* Home hero narrative: clean gradient background */}
          <div className="absolute inset-0 bg-[radial-gradient(1500px_900px_at_50%_10%,rgba(10,18,32,0.95),rgba(0,0,0,1)_55%)]" />
          <div className="absolute inset-0 opacity-[0.22] [background-image:linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.045)_1px,transparent_1px)] [background-size:160px_160px]" />

          {/* Subtle accent glows - static, no animation */}
          <div className="absolute inset-0 bg-[radial-gradient(800px_600px_at_8%_45%,rgba(97,221,152,0.08),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(800px_600px_at_92%_45%,rgba(198,98,191,0.10),transparent_50%)]" />

          {/* Preserve negative space around the logo */}
          <div className="absolute left-1/2 top-[44%] h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(0,0,0,0.55),transparent_58%)]" />
        </>
      )}

      {isFocusAreas && (
        <>
          <div className="absolute inset-0 bg-[radial-gradient(1300px_820px_at_55%_12%,rgba(8,14,24,0.92),rgba(0,0,0,1)_58%)]" />
          <div className="absolute inset-0 opacity-[0.24] [background-image:linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] [background-size:170px_170px]" />
          {/* Enhanced soft beams */}
          <div className="absolute -left-32 top-8 h-[620px] w-[620px] rounded-full bg-[rgba(97,221,152,0.08)] blur-[100px]" />
          <div className="absolute -right-32 bottom-4 h-[720px] w-[720px] rounded-full bg-[rgba(198,98,191,0.07)] blur-[120px]" />
          <div className="absolute left-1/4 top-1/3 h-[400px] w-[400px] rounded-full bg-[rgba(198,98,191,0.06)] blur-[90px]" />
          <div className="absolute right-1/4 bottom-1/3 h-[400px] w-[400px] rounded-full bg-[rgba(97,221,152,0.05)] blur-[90px]" />
        </>
      )}

      {isCollab && (
        <>
          <div className="absolute inset-0 bg-[radial-gradient(1400px_880px_at_50%_12%,rgba(6,12,18,0.90),rgba(0,0,0,1)_60%)]" />
          <MolecularSigil className="absolute left-1/2 top-[66%] h-[780px] w-[1150px] -translate-x-1/2 -translate-y-1/2 opacity-[0.32] blur-[0.5px] bg-drift-slow hidden md:block" />
          {/* Enhanced accent orbs */}
          <div className="absolute left-[10%] top-[20%] h-[500px] w-[500px] rounded-full bg-[rgba(198,98,191,0.10)] blur-[100px]" />
          <div className="absolute right-[10%] top-[30%] h-[450px] w-[450px] rounded-full bg-[rgba(97,221,152,0.08)] blur-[95px]" />
          <div className="absolute left-[30%] bottom-[15%] h-[380px] w-[380px] rounded-full bg-[rgba(97,221,152,0.07)] blur-[85px]" />
          <div className="absolute right-[25%] bottom-[20%] h-[420px] w-[420px] rounded-full bg-[rgba(198,98,191,0.08)] blur-[90px]" />
        </>
      )}

      {/* fallback - ENHANCED */}
      {!isHome && !isFocusAreas && !isCollab && (
        <>
          <div className="absolute inset-0 bg-[radial-gradient(1300px_780px_at_50%_12%,rgba(8,14,22,0.80),rgba(0,0,0,1)_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(1000px_580px_at_18%_68%,rgba(97,221,152,0.12),transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(1150px_650px_at_82%_58%,rgba(198,98,191,0.14),transparent_58%)]" />
        </>
      )}

      {/* matte texture */}
      <div className="absolute inset-0 noise-overlay" />

      {/* vignette (reduced so cell glow is visible) */}
      <div className="absolute inset-0 bg-[radial-gradient(1200px_750px_at_50%_28%,transparent_58%,rgba(0,0,0,0.35))]" />
    </div>
  );
};

const clamp01 = (n) => Math.max(0, Math.min(1, n));
const lerp = (a, b, t) => a + (b - a) * t;

// Static Home Logo Component (no scroll animation)
const HomeLogoStatic = ({ onHomeClick, logoSrc }) => {
  return (
    <button
      type="button"
      onClick={onHomeClick}
      className="home-logo-static group"
      aria-label="Cellza"
    >
      <img
        src={logoSrc || logo}
        alt=""
        width={600}
        height={203}
        decoding="async"
        className="home-logo-static__img"
        draggable={false}
      />
    </button>
  );
};


const cx = (...classes) => classes.filter(Boolean).join(" ");

const useActivePath = () => {
  const { pathname } = useLocation();
  return pathname;
};

export const SiteLayout = ({ children }) => {
  const activePath = useActivePath();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  const nav = useMemo(() => mockContent.nav, []);

  const logoSrc = logo;

  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || 0;
      setScrolled(y > 8);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  return (
    <div className="min-h-screen bg-transparent text-white selection:bg-white/15 selection:text-white">
      {/* background layers (route-aware) */}
      <RouteBackground />

      <header
        className={cx(
          "sticky top-0 z-50 border-b",
          scrolled
            ? "border-white/10 bg-black/55 backdrop-blur-[18px]"
            : "border-white/5 bg-black/20 backdrop-blur-[12px]",
        )}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 md:px-8">
          {/* Static header logo */}
          <button
            type="button"
            onClick={() => {
              window.scrollTo(0, 0);
              navigate("/");
            }}
            className="flex items-center gap-2"
            aria-label="Go to home"
          >
            <img
              src={logoSrc}
              alt="Cellza"
              width={600}
              height={203}
              decoding="async"
              className="h-7 w-auto object-contain opacity-90"
            />
          </button>

          <nav className="hidden items-center gap-7 md:flex">
            {nav.map((item) => {
              const isActive = activePath === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => window.scrollTo(0, 0)}
                  className={cx(
                    "text-sm tracking-wide transition-colors duration-200",
                    isActive ? "text-white" : "text-white/65 hover:text-white",
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="secondary"
                  className="rounded-sm border border-white/12 bg-white/5 text-white hover:bg-white hover:text-black"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent className="border-white/10 bg-black/80 text-white backdrop-blur-[18px]">
                <SheetHeader>
                  <SheetTitle className="text-white">Menu</SheetTitle>
                </SheetHeader>
                <div className="mt-6 flex flex-col gap-2">
                  {nav.map((item) => (
                    <Button
                      key={item.to}
                      variant="ghost"
                      className={cx(
                        "justify-start rounded-sm px-3 py-6 text-base",
                        activePath === item.to ? "bg-white/10" : "hover:bg-white/10",
                      )}
                      onClick={() => {
                        window.scrollTo(0, 0);
                        navigate(item.to);
                      }}
                    >
                      {item.label}
                      <ExternalLink className="ml-auto h-4 w-4 opacity-70" />
                    </Button>
                  ))}
                  <Separator className="my-2 bg-white/10" />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main>{children}</main>

      <footer className="border-t border-white/10 bg-black/80" style={{ minHeight: 120 }}>
        <div className="mx-auto max-w-6xl px-5 py-10 md:px-8">
          <div className="grid gap-8 md:grid-cols-3 md:items-center">
            {/* Left Column - Logo and Copyright */}
            <div className="flex items-center gap-3">
              <img
                src={logoSrc}
                alt="Cellza Bio"
                width={600}
                height={203}
                decoding="async"
                className="h-6 w-auto object-contain opacity-90"
              />
              <span className="text-xs tracking-wide text-white/55">
                © 2026 Cellza Bio. All rights reserved.
              </span>
            </div>

            {/* Center Column - Tagline */}
            <div className="text-center">
              <span className="text-sm text-white/60">
                Based in India. Building for the world.
              </span>
            </div>

            {/* Right Column - Contact & Social Links */}
            <div className="flex items-center gap-4 justify-start md:justify-end">
              <a
                href="mailto:partnerships@cellza.in"
                className="text-sm text-white/60 hover:text-white transition-colors"
              >
                partnerships@cellza.in
              </a>
              <a
                href="https://www.linkedin.com/company/cellzabio/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-sm border border-white/10 bg-white/5 text-white/60 transition-colors hover:border-white/20 hover:bg-white/10 hover:text-white"
                aria-label="Follow Cellza on LinkedIn"
              >
                <Linkedin className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
